import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Users, Mail, MailX, FileText, Search, LogOut, Save, X, Download, Anchor, ClipboardList, Trash2, Package } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all"); // all, newsletter_yes, newsletter_no
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [loading, setLoading] = useState(false);
  const [showNewsletter, setShowNewsletter] = useState(false);
  const [newsletterSubject, setNewsletterSubject] = useState("");
  const [newsletterContent, setNewsletterContent] = useState("");
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const [newsletterResult, setNewsletterResult] = useState(null);
  const [activeTab, setActiveTab] = useState("registrations"); // registrations | storage | deposits | direct
  const [storageForms, setStorageForms] = useState([]);
  const [depositForms, setDepositForms] = useState([]);
  const [directRegistrations, setDirectRegistrations] = useState([]);
  const [depositFull, setDepositFull] = useState(false);

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem("ppsc_admin_token");
    if (token) {
      setIsLoggedIn(true);
      loadData();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoading(true);
    
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password })
      });
      
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem("ppsc_admin_token", data.token);
        setIsLoggedIn(true);
        loadData();
      } else {
        setLoginError("Credenziali non valide");
      }
    } catch (err) {
      setLoginError("Errore di connessione");
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("ppsc_admin_token");
    setIsLoggedIn(false);
    setRegistrations([]);
    setStats(null);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Load stats
      const statsRes = await fetch(`${API}/admin/stats`);
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
      
      // Load registrations
      let url = `${API}/admin/registrations`;
      if (filter === "newsletter_yes") url += "?newsletter=yes";
      else if (filter === "newsletter_no") url += "?newsletter=no";
      
      const regRes = await fetch(url);
      if (regRes.ok) {
        const regData = await regRes.json();
        setRegistrations(regData);
      }

      // Load storage forms
      const token = localStorage.getItem("ppsc_admin_token");
      if (token) {
        const storageRes = await fetch(`${API}/admin/storage?token=${token}`);
        if (storageRes.ok) {
          const storageData = await storageRes.json();
          setStorageForms(storageData);
        }

        // Load deposit forms
        const depositRes = await fetch(`${API}/admin/deposits?token=${token}`);
        if (depositRes.ok) {
          const depositData = await depositRes.json();
          setDepositForms(depositData);
        }

        // Load deposit full status
        const fullRes = await fetch(`${API}/deposit-full`);
        if (fullRes.ok) {
          const fullData = await fullRes.json();
          setDepositFull(fullData.deposit_full);
        }

        // Load direct registrations
        const directRes = await fetch(`${API}/admin/direct-registrations?token=${token}`);
        if (directRes.ok) {
          const directData = await directRes.json();
          setDirectRegistrations(directData);
        }
      }
    } catch (err) {
      console.error("Error loading data:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isLoggedIn) {
      loadData();
    }
  }, [filter, isLoggedIn]);

  const saveNote = async () => {
    if (!selectedRegistration) return;
    
    try {
      const res = await fetch(`${API}/admin/registrations/${selectedRegistration.id}/note`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note: noteText })
      });
      
      if (res.ok) {
        // Update local state
        setRegistrations(prev => prev.map(r => 
          r.id === selectedRegistration.id 
            ? { ...r, admin_note: noteText }
            : r
        ));
        setSelectedRegistration(null);
        setNoteText("");
      }
    } catch (err) {
      console.error("Error saving note:", err);
    }
  };

  const deleteRegistration = async (regId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa iscrizione?")) return;
    const token = localStorage.getItem("ppsc_admin_token");
    try {
      const res = await fetch(`${API}/admin/registrations/${regId}?token=${token}`, { method: "DELETE" });
      if (res.ok) {
        setRegistrations(prev => prev.filter(r => r.id !== regId));
      }
    } catch (err) {
      console.error("Error deleting registration:", err);
    }
  };

  const deleteStorageForm = async (sfId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo noleggio?")) return;
    const token = localStorage.getItem("ppsc_admin_token");
    try {
      const res = await fetch(`${API}/admin/storage/${sfId}?token=${token}`, { method: "DELETE" });
      if (res.ok) {
        setStorageForms(prev => prev.filter(s => s.id !== sfId));
      }
    } catch (err) {
      console.error("Error deleting storage form:", err);
    }
  };

  const deleteDeposit = async (depId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo deposito?")) return;
    const token = localStorage.getItem("ppsc_admin_token");
    try {
      const res = await fetch(`${API}/admin/deposits/${depId}?token=${token}`, { method: "DELETE" });
      if (res.ok) {
        setDepositForms(prev => prev.filter(d => d.id !== depId));
      }
    } catch (err) {
      console.error("Error deleting deposit:", err);
    }
  };

  const toggleDepositFull = async () => {
    const token = localStorage.getItem("ppsc_admin_token");
    try {
      const res = await fetch(`${API}/admin/deposit-full?token=${token}`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        setDepositFull(data.deposit_full);
      }
    } catch (err) {
      console.error("Error toggling deposit full:", err);
    }
  };

  const filteredRegistrations = registrations.filter(r => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      r.name?.toLowerCase().includes(term) ||
      r.surname?.toLowerCase().includes(term) ||
      r.email?.toLowerCase().includes(term) ||
      r.phone?.includes(term)
    );
  });

  const filteredDirectRegistrations = directRegistrations.filter(r => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      r.name?.toLowerCase().includes(term) ||
      r.surname?.toLowerCase().includes(term) ||
      r.email?.toLowerCase().includes(term) ||
      r.phone?.includes(term) ||
      r.fiscal_code?.toLowerCase().includes(term) ||
      r.nationality?.toLowerCase().includes(term)
    );
  });

  const handleSendNewsletter = async () => {
    if (!newsletterSubject.trim() || !newsletterContent.trim()) return;
    setSendingNewsletter(true);
    setNewsletterResult(null);
    try {
      const res = await fetch(`${API}/admin/send-newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subject: newsletterSubject, html_content: newsletterContent })
      });
      const data = await res.json();
      setNewsletterResult(data);
      if (data.success && data.sent > 0) {
        setNewsletterSubject("");
        setNewsletterContent("");
      }
    } catch (err) {
      setNewsletterResult({ success: false, message: "Errore di rete" });
    }
    setSendingNewsletter(false);
  };

  const [excelDateFrom, setExcelDateFrom] = useState("");
  const [excelDateTo, setExcelDateTo] = useState("");
  const [showExcelDialog, setShowExcelDialog] = useState(false);

  const handleExportExcel = () => {
    const token = localStorage.getItem("ppsc_admin_token");
    let url = `${API}/admin/export-excel?token=${token}`;
    if (excelDateFrom) url += `&date_from=${excelDateFrom}`;
    if (excelDateTo) url += `&date_to=${excelDateTo}`;
    window.open(url, '_blank');
    setShowExcelDialog(false);
  };

  // Login Page
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-600 to-cyan-700 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Admin PPSC</h1>
            <p className="text-gray-500 text-sm mt-1">Porto Pollo Academy</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                required
                data-testid="admin-username-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                data-testid="admin-password-input"
              />
            </div>
            
            {loginError && (
              <p className="text-red-500 text-sm text-center">{loginError}</p>
            )}
            
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
              disabled={loading}
              data-testid="admin-login-button"
            >
              {loading ? "Accesso..." : "Accedi"}
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-600 to-cyan-700 text-white p-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">🏄‍♀️ Admin PPSC</h1>
            <p className="text-teal-100 text-sm">Gestione Iscrizioni</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setShowExcelDialog(!showExcelDialog)}
              variant="ghost"
              className="text-white hover:bg-white/20"
              data-testid="export-excel-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Excel
            </Button>
            <Button
              onClick={() => setShowNewsletter(!showNewsletter)}
              variant="ghost"
              className="text-white hover:bg-white/20"
              data-testid="newsletter-toggle-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Newsletter
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-white hover:bg-white/20"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>
      </header>

      {/* Excel Export Dialog */}
      {showExcelDialog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowExcelDialog(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg text-gray-900 mb-4">Esporta Excel</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-sm text-gray-600">Da (opzionale)</Label>
                <Input type="date" value={excelDateFrom} onChange={e => setExcelDateFrom(e.target.value)} />
              </div>
              <div>
                <Label className="text-sm text-gray-600">A (opzionale)</Label>
                <Input type="date" value={excelDateTo} onChange={e => setExcelDateTo(e.target.value)} />
              </div>
              <p className="text-xs text-gray-400">Lascia vuoto per scaricare tutto</p>
            </div>
            <div className="flex gap-3 mt-4">
              <Button variant="outline" onClick={() => setShowExcelDialog(false)} className="flex-1">Annulla</Button>
              <Button onClick={handleExportExcel} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white">
                <Download className="w-4 h-4 mr-2" /> Scarica
              </Button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.total_registrations}</p>
                  <p className="text-xs text-gray-500">Totale Iscritti</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.newsletter_subscribers}</p>
                  <p className="text-xs text-gray-500">Newsletter Sì</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <MailX className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.newsletter_not_subscribed}</p>
                  <p className="text-xs text-gray-500">Newsletter No</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.media_consent_given}</p>
                  <p className="text-xs text-gray-500">Consenso Media</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Newsletter Panel */}
        <AnimatePresence>
          {showNewsletter && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6 overflow-hidden"
            >
              <h2 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
                <Mail className="w-5 h-5 text-green-600" />
                Invia Newsletter
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Invia email solo a chi ha accettato la newsletter ({stats?.newsletter_subscribers || 0} iscritti).
                Usa <code className="bg-gray-100 px-1 rounded">{"{name}"}</code> per personalizzare con il nome.
              </p>
              <div className="space-y-3">
                <Input
                  placeholder="Oggetto email..."
                  value={newsletterSubject}
                  onChange={(e) => setNewsletterSubject(e.target.value)}
                  data-testid="newsletter-subject"
                />
                <Textarea
                  placeholder="Contenuto email (HTML supportato)..."
                  value={newsletterContent}
                  onChange={(e) => setNewsletterContent(e.target.value)}
                  rows={6}
                  data-testid="newsletter-content"
                />
                <div className="flex items-center gap-3">
                  <Button
                    onClick={handleSendNewsletter}
                    disabled={sendingNewsletter || !newsletterSubject.trim() || !newsletterContent.trim()}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="send-newsletter-btn"
                  >
                    {sendingNewsletter ? "Invio in corso..." : "Invia a tutti gli iscritti"}
                  </Button>
                  <Button
                    onClick={() => { setShowNewsletter(false); setNewsletterResult(null); }}
                    variant="outline"
                  >
                    Chiudi
                  </Button>
                </div>
                {newsletterResult && (
                  <div className={`text-sm p-3 rounded-lg ${newsletterResult.success && newsletterResult.sent > 0 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                    {newsletterResult.success
                      ? `Email inviate: ${newsletterResult.sent}/${newsletterResult.total_subscribers}`
                      : newsletterResult.message || "Nessun iscritto alla newsletter"}
                    {newsletterResult.errors?.length > 0 && (
                      <p className="mt-1 text-xs">Errori: {newsletterResult.errors.join(", ")}</p>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tab Switcher */}
        <div className="flex gap-1 bg-white rounded-xl p-1.5 shadow-sm border border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab("registrations")}
            data-testid="tab-registrations"
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === "registrations"
                ? "bg-teal-600 text-white shadow-sm"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <ClipboardList className="w-4 h-4" />
            Iscrizioni ({registrations.length})
          </button>
          <button
            onClick={() => setActiveTab("storage")}
            data-testid="tab-storage"
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === "storage"
                ? "bg-sky-600 text-white shadow-sm"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <Anchor className="w-4 h-4" />
            Noleggi ({storageForms.length})
          </button>
          <button
            onClick={() => setActiveTab("deposits")}
            data-testid="tab-deposits"
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === "deposits"
                ? "bg-emerald-600 text-white shadow-sm"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <Package className="w-4 h-4" />
            Depositi ({depositForms.length})
          </button>
          <button
            onClick={() => setActiveTab("direct")}
            data-testid="tab-direct"
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === "direct"
                ? "bg-purple-600 text-white shadow-sm"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <Users className="w-4 h-4" />
            Dirette ({directRegistrations.length})
          </button>
        </div>

        {activeTab === "registrations" && (
        <>
        {/* Filters and Search */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex gap-2">
              <Button
                onClick={() => setFilter("all")}
                variant={filter === "all" ? "default" : "outline"}
                size="sm"
                data-testid="filter-all-button"
              >
                Tutti
              </Button>
              <Button
                onClick={() => setFilter("newsletter_yes")}
                variant={filter === "newsletter_yes" ? "default" : "outline"}
                size="sm"
                className={filter === "newsletter_yes" ? "bg-green-600 hover:bg-green-700" : ""}
                data-testid="filter-newsletter-yes-button"
              >
                <Mail className="w-4 h-4 mr-1" />
                Newsletter Sì
              </Button>
              <Button
                onClick={() => setFilter("newsletter_no")}
                variant={filter === "newsletter_no" ? "default" : "outline"}
                size="sm"
                className={filter === "newsletter_no" ? "bg-red-600 hover:bg-red-700" : ""}
                data-testid="filter-newsletter-no-button"
              >
                <MailX className="w-4 h-4 mr-1" />
                Newsletter No
              </Button>
            </div>
            
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Cerca per nome, email o telefono..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="admin-search-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Registrations List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contatto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lezione</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">📧</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">📸</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                      Caricamento...
                    </td>
                  </tr>
                ) : filteredRegistrations.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                      Nessuna iscrizione trovata
                    </td>
                  </tr>
                ) : (
                  filteredRegistrations.map((reg) => (
                    <tr key={reg.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {reg.name} {reg.surname}
                          {reg.csen_clicked && <span className="ml-1 text-green-600 text-xs font-bold" title="CSEN compilato">CSEN</span>}
                        </div>
                        {reg.nationality && reg.nationality !== 'Italia' && reg.nationality !== 'Italiana' && (
                          <div className="text-[10px] text-gray-400">{reg.nationality}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-600">{reg.email}</div>
                        <a 
                          href={`https://wa.me/${reg.phone?.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-green-600 hover:underline"
                        >
                          {reg.phone}
                        </a>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-teal-100 text-teal-800 text-xs rounded-full">
                          {reg.lesson_type}
                        </span>
                        <div className="text-xs text-gray-500 mt-1">{reg.duration}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-600">{reg.preferred_date || '-'}</div>
                        <div className="text-xs text-gray-400">{reg.preferred_time || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {reg.accepts_newsletter ? (
                          <span className="text-green-600 text-lg">✓</span>
                        ) : (
                          <span className="text-red-400 text-lg">✗</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {reg.accepts_media_consent ? (
                          <span className="text-green-600 text-lg">✓</span>
                        ) : (
                          <span className="text-red-400 text-lg">✗</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="max-w-xs truncate text-sm text-gray-600">
                          {reg.admin_note || <span className="text-gray-400 italic">Nessuna nota</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex gap-2 justify-center">
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`note-btn-${reg.id}`}
                            onClick={() => {
                              setSelectedRegistration(reg);
                              setNoteText(reg.admin_note || "");
                            }}
                          >
                            ✏️ Nota
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`download-pdf-btn-${reg.id}`}
                            className="text-teal-700 border-teal-300 hover:bg-teal-50"
                            onClick={() => {
                              window.open(`${API}/admin/registrations/${reg.id}/pdf`, '_blank');
                            }}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            PDF
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-purple-700 border-purple-300 hover:bg-purple-50"
                            onClick={() => {
                              const params = new URLSearchParams({
                                surname: reg.surname || '',
                                name: reg.name || '',
                                sex: reg.sex || '',
                                birth_date: reg.birth_date || '',
                                birth_place: reg.birth_place || '',
                                birth_province: reg.birth_province || '',
                                fiscal_code: reg.fiscal_code || '',
                                email: reg.email || '',
                                phone: reg.phone || '',
                                nationality: reg.nationality || 'Italia',
                                // Conservative: only mark as foreign if nationality is EXPLICITLY non-Italian.
                                // Missing fiscal_code alone is NOT enough (Italian users may not have provided it).
                                is_foreign: (reg.nationality && reg.nationality.trim() && reg.nationality !== 'Italia' && reg.nationality !== 'Italiana') ? '1' : '0'
                              });
                              // Mark as CSEN clicked
                              const token = localStorage.getItem("ppsc_admin_token");
                              fetch(`${API}/admin/registrations/${reg.id}/csen-clicked?token=${token}`, { method: "POST" }).then(() => loadData());
                              window.open(`/csen-fill?${params.toString()}`, '_blank');
                            }}
                          >
                            CSEN
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`delete-reg-btn-${reg.id}`}
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => deleteRegistration(reg.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>
        )}

        {/* Storage/Rental Forms Tab */}
        {activeTab === "storage" && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-sky-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Tesserato</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attrezzatura</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orari</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Totale</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-gray-500">Caricamento...</td>
                    </tr>
                  ) : storageForms.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-gray-500">Nessun modulo noleggio trovato</td>
                    </tr>
                  ) : (
                    storageForms.map((sf) => {
                      // Support both old and new format
                      const equipmentLabels = [];
                      if (sf.equipment && Array.isArray(sf.equipment) && sf.equipment.length > 0) {
                        sf.equipment.forEach(eq => equipmentLabels.push(eq.type || eq.duration || ""));
                      } else {
                        if (sf.base_sail || sf.base_board) equipmentLabels.push("Base");
                        if (sf.funboard_sail || sf.funboard_board) equipmentLabels.push("Funboard");
                        if (sf.foil_wing_sail || sf.foil_wing_board) equipmentLabels.push("Foil/Wing");
                        if (sf.kayak_1_seat || sf.kayak_2_seat || sf.kayak_3_seat) equipmentLabels.push("Kayak");
                        if (sf.sup_surf) equipmentLabels.push("SUP");
                        if (sf.accessory_wetsuit || sf.accessory_harness) equipmentLabels.push("Accessori");
                      }
                      const totalDisplay = sf.total_cost ? `${sf.total_cost}€` : (sf.total_price ? `${sf.total_price}€` : '-');
                      return (
                        <tr key={sf.id} className="hover:bg-gray-50" data-testid={`storage-row-${sf.id}`}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{sf.name} {sf.surname}</div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sf.is_member ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                              {sf.is_member ? "Si" : "No"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{sf.date || '-'}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {equipmentLabels.map((eq, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-sky-100 text-sky-700 text-xs rounded-full">{eq}</span>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {sf.start_time_1 && <div>{sf.start_time_1} - {sf.end_time_1}</div>}
                            {sf.start_time_2 && <div>{sf.start_time_2} - {sf.end_time_2}</div>}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="font-bold text-gray-800">{totalDisplay}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex gap-2 justify-center">
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`storage-pdf-btn-${sf.id}`}
                                className="text-sky-700 border-sky-300 hover:bg-sky-50"
                                onClick={() => {
                                  const token = localStorage.getItem("ppsc_admin_token");
                                  window.open(`${API}/admin/storage/${sf.id}/pdf?token=${token}`, '_blank');
                                }}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                PDF
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`storage-delete-btn-${sf.id}`}
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => deleteStorageForm(sf.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Deposits Tab */}
        {activeTab === "deposits" && (
          <>
            {/* Deposit Full Toggle */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-6 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800">Stato Deposito</h3>
                <p className="text-sm text-gray-500">{depositFull ? "Il deposito e' attualmente PIENO. Alessia informera' i clienti." : "Il deposito ha posti disponibili."}</p>
              </div>
              <button
                onClick={toggleDepositFull}
                data-testid="toggle-deposit-full"
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${depositFull ? "bg-red-500 text-white hover:bg-red-600" : "bg-emerald-500 text-white hover:bg-emerald-600"}`}
              >
                {depositFull ? "PIENO - Clicca per riaprire" : "DISPONIBILE - Clicca per chiudere"}
              </button>
            </div>

            {/* Deposit Forms Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-emerald-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Tesserato</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contatto</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Periodo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deposito</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Prezzo</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">Caricamento...</td>
                      </tr>
                    ) : depositForms.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">Nessuna richiesta di deposito trovata</td>
                      </tr>
                    ) : (
                      depositForms.map((dep) => (
                        <tr key={dep.id} className="hover:bg-gray-50" data-testid={`deposit-row-${dep.id}`}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{dep.name} {dep.surname}</div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${dep.is_member ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                              {dep.is_member ? "Si" : "No"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {dep.phone && <div>{dep.phone}</div>}
                            {dep.email && <div className="text-gray-400">{dep.email}</div>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {dep.start_date && dep.end_date ? (
                              <div>{dep.start_date} — {dep.end_date}</div>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">{dep.deposit_label || '-'}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="font-bold text-gray-800">{dep.deposit_price ? `${dep.deposit_price}€` : '-'}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`deposit-delete-btn-${dep.id}`}
                              className="text-red-600 border-red-300 hover:bg-red-50"
                              onClick={() => deleteDeposit(dep.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {activeTab === "direct" && (
          <>
            {/* Search */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Cerca per nome, cognome, email, telefono, codice fiscale o nazionalita'..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="direct-search-input"
                />
                {searchTerm && (
                  <div className="mt-2 text-xs text-gray-500">
                    {filteredDirectRegistrations.length} risultati su {directRegistrations.length}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-purple-700">Nome</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-purple-700">Contatti</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-purple-700">Eta'/Peso</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-purple-700">Cert. Med.</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-purple-700">Data</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-purple-700">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredDirectRegistrations.length === 0 ? (
                      <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-400">
                        {searchTerm ? `Nessun risultato per "${searchTerm}"` : "Nessuna iscrizione diretta"}
                      </td></tr>
                    ) : (
                      filteredDirectRegistrations.map(reg => (
                        <tr key={reg.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-800">
                              {reg.name} {reg.surname}
                              {reg.csen_clicked && <span className="ml-1 text-green-600 text-xs font-bold" title="CSEN compilato">CSEN</span>}
                            </div>
                            {reg.sex && <div className="text-xs text-gray-400">{reg.sex === 'M' ? 'Maschio' : 'Femmina'}</div>}
                            {reg.nationality && reg.nationality !== 'Italia' && reg.nationality !== 'Italiana' && (
                              <div className="text-[10px] text-gray-400">{reg.nationality}</div>
                            )}
                            {reg.is_minor && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Minore</span>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {reg.phone && <div>{reg.phone}</div>}
                            {reg.email && <div className="text-gray-400">{reg.email}</div>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {reg.age && <div>{reg.age} anni</div>}
                            {reg.weight && <div>{reg.weight} kg</div>}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 text-xs rounded-full ${reg.has_medical_certificate ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {reg.has_medical_certificate ? 'Si' : 'No'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {reg.created_at ? new Date(reg.created_at).toLocaleDateString('it-IT') : '-'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex gap-2 justify-center">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-teal-700 border-teal-300 hover:bg-teal-50"
                                onClick={() => {
                                  const token = localStorage.getItem("ppsc_admin_token");
                                  window.open(`${API}/admin/direct-registrations/${reg.id}/pdf?token=${token}`, '_blank');
                                }}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                PDF
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-purple-700 border-purple-300 hover:bg-purple-50"
                                onClick={() => {
                                  const params = new URLSearchParams({
                                    surname: reg.surname || '',
                                    name: reg.name || '',
                                    sex: reg.sex || '',
                                    birth_date: reg.birth_date || '',
                                    birth_place: reg.birth_place || '',
                                    birth_province: reg.birth_province || '',
                                    fiscal_code: reg.fiscal_code || '',
                                    email: reg.email || '',
                                    phone: reg.phone || '',
                                    nationality: reg.nationality || 'Italia',
                                    // Conservative: only mark as foreign if nationality is EXPLICITLY non-Italian.
                                    is_foreign: (reg.nationality && reg.nationality.trim() && reg.nationality !== 'Italia' && reg.nationality !== 'Italiana') ? '1' : '0'
                                  });
                                  // Mark as CSEN clicked
                                  const token = localStorage.getItem("ppsc_admin_token");
                                  fetch(`${API}/admin/direct-registrations/${reg.id}/csen-clicked?token=${token}`, { method: "POST" }).then(() => loadData());
                                  window.open(`/csen-fill?${params.toString()}`, '_blank');
                                }}
                              >
                                CSEN
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                onClick={async () => {
                                  const token = localStorage.getItem("ppsc_admin_token");
                                  if (window.confirm("Eliminare questa iscrizione diretta?")) {
                                    await fetch(`${API}/admin/direct-registrations/${reg.id}?token=${token}`, { method: "DELETE" });
                                    loadData();
                                  }
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>

      {/* Note Modal */}
      <AnimatePresence>
        {selectedRegistration && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedRegistration(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-800">
                  Nota per {selectedRegistration.name} {selectedRegistration.surname}
                </h3>
                <button
                  onClick={() => setSelectedRegistration(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <Textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Scrivi una nota per questa prenotazione..."
                rows={4}
                className="mb-4"
              />
              
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setSelectedRegistration(null)}
                >
                  Annulla
                </Button>
                <Button
                  onClick={saveNote}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Salva
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
